import React, { useState, useEffect, useRef, useMemo } from 'react'
import { AppInput, IcoPosition } from '../AppInput/AppInput'
import { AppScrollWrapperNew } from '../../widgets/AppScrollWrapper/AppScrollWrapper'
import Checkbox from '../checkbox/Checkbox'
import AppBtn, { AppBtnColor, AppBtnRadius, AppBtnSize } from '../btn/AppBtn'
import { paginationHelper } from '../../utils/paginationHelper'
import { AppLoader, LoaderType } from '../AppLoader'
import { useOutsideClick } from '../../utils/useOutsideClick'
import { useTranslation } from 'react-i18next'
import { IWithPaginationRequestSearch } from '../../core/api/dto/BaseDto'
import { AppImagePopup, IAppImagePopup } from '../AppImagePopup/AppImagePopup'
import { AppDropdownChooseItems } from './AppDropdownChooseItems/AppDropdownChooseItems'
import './AppDropdown.scss'

export interface IExtraOptions {
  isArchived?: boolean
  archived?: boolean
}

interface IAppMultiDropdownWithPhotoAndPagination<T, TKey> {
  value: T[]
  propToShowInList?: TKey
  propToShowInInput: TKey
  sparePropToShowInList?: TKey
  extraPropToShowInList?: TKey
  propToShowPhotoS3?: TKey
  propToShowPhoto?: TKey
  propToShowWheight?: TKey
  getterData: (
    params: IWithPaginationRequestSearch | any | null,
    extraParams?: { skip?: boolean },
  ) => any
  onChange: (
    value: T[],
  ) => void | React.Dispatch<React.SetStateAction<[] | T[]>>
  resetValueHandler?: () => void
  label?: string
  placeholder?: string
  extraOptions?: IExtraOptions
  disabled?: boolean
  error?: string | null
  id?: string
  emptyListMessage?: string
  require?: boolean
  border?: boolean
  fullWidth?: boolean
  minWidth?: boolean
  showArchivedItems?: boolean
  skip?: boolean
  required?: boolean
}

export const AppMultiDropdownWithPhotoAndPagination = <
  T,
  TKey extends keyof T,
>({
  value,
  onChange,
  propToShowInList,
  propToShowInInput,
  extraPropToShowInList,
  sparePropToShowInList,
  propToShowPhotoS3,
  propToShowPhoto,
  propToShowWheight,
  getterData,
  label,
  placeholder,
  extraOptions,
  disabled,
  error,
  emptyListMessage,
  resetValueHandler,
  require,
  border,
  fullWidth,
  minWidth,
  id,
  showArchivedItems,
  skip,
  required,
}: IAppMultiDropdownWithPhotoAndPagination<T, TKey>) => {
  const { t } = useTranslation()
  const [active, setActive] = useState<boolean>(false)
  const [currentData, setCurrentData] = useState<T[] | []>([])
  const [isLoadMore, setIsLoadMore] = useState(false)
  const [inputSearch, setInputSearch] = useState('')
  const [pageParams, setPageParams] = useState<IWithPaginationRequestSearch>({
    limit: 40,
    offset: 0,
  })
  const [resetData, setResetData] = useState<boolean>(false)
  const [imagePopup, setImagePopup] = useState<IAppImagePopup>({
    active: false,
    photo: null,
    title: null,
  })

  const refDropDown = useRef<HTMLDivElement>(null)
  const refInput = useRef<HTMLInputElement>(null)

  const params = inputSearch
    ? { ...pageParams, search: inputSearch }
    : pageParams
  const { data, isFetching } = getterData(
    !!params
      ? { ...extraOptions, ...params, is_archived: false }
      : !!extraOptions
      ? extraOptions
      : null,
    { skip: !!skip || !params },
  )

  useEffect(() => {
    if (!!extraOptions) {
      setInputSearch('')
      setPageParams({
        limit: 40,
        offset: 0,
      })
      if (!!data && !!data.results) {
        setResetData(true)
      }
    }
  }, [JSON.stringify(extraOptions)])

  useEffect(() => {
    if (!data) return
    if (isLoadMore && !resetData) {
      setCurrentData((prev) => [...prev, ...data.results])
      setIsLoadMore(false)
    } else {
      setCurrentData(data.results)
    }
    if (resetData) {
      setResetData(false)
    }
  }, [data, isLoadMore, resetData])

  const nextPageHandler = () => {
    if (!data?.next) return
    const nextPageParams = paginationHelper({ next: data.next })
    setPageParams(nextPageParams)
    setIsLoadMore(true)
  }

  useOutsideClick(refDropDown, () => setActive(false), active)

  const onChangeHandler = (newItem: T) => {
    const duplicate = value.find((item) => item[propToShowInInput] === newItem[propToShowInInput])
    if (!!duplicate) {
      const removeDuplicate = value.filter(
        (item) => item[propToShowInInput] !== duplicate[propToShowInInput],
      )
      onChange(removeDuplicate)
      return
    }
    onChange([...value, newItem])
  }

  const onDeleteFromList = (id: string | number) => {
    const filtered = value.filter((item) => item[propToShowInInput] !== id)
    onChange(filtered)
  }

  const multiValueObj: { [key: string]: boolean } | undefined = useMemo(
    () =>
      value?.reduce((obj: { [key: string]: boolean }, el: T) => {
        const key = `${el[propToShowInInput]}`
        return { ...obj, [key]: true }
      }, {} as { [key: string]: boolean }),
    [value, propToShowInInput],
  )

  const closeImagePopup = () => {
    setImagePopup({ active: false, photo: null, title: null })
  }

  const inputValue =
    value.length === 0 ? null : `${t('Выбрано')} : ${value.length}`

  // Не закрываем дропдаун при закрытии попапа
  useEffect(() => {
    if (!imagePopup.active) return

    const handlePopupClick = (e: Event) => {
      e.stopPropagation()
    }

    const timeoutId = setTimeout(() => {
      const popupRoot = document.getElementById('popup-root')
      if (popupRoot) {
        popupRoot.addEventListener('click', handlePopupClick, false)
        popupRoot.addEventListener('touchend', handlePopupClick, false)
      }
    }, 10)

    return () => {
      clearTimeout(timeoutId)
      const popupRoot = document.getElementById('popup-root')
      if (popupRoot) {
        popupRoot.removeEventListener('click', handlePopupClick, false)
        popupRoot.removeEventListener('touchend', handlePopupClick, false)
      }
    }
  }, [imagePopup.active])

  useEffect(() => {
    if (!refDropDown.current) return

    const handlePhotoClick = (e: Event) => {
      const target = e.target as HTMLElement
      const photoWrap = target.closest('.dropdown-photo-wrap')

      if (photoWrap && photoWrap.classList.contains('dropdown-photo-wrap')) {
        e.stopPropagation()
        e.preventDefault()

        const img = photoWrap.querySelector(
          '.dropdpwn-photo',
        ) as HTMLImageElement
        const valueItem = photoWrap.closest('.value-item-multi-photo')
        const titleElement = valueItem?.querySelector('.dropdown-photo-title')

        if (img && img.src) {
          const title = titleElement?.textContent || ''
          setImagePopup({ active: true, photo: img.src, title })
        }
      }
    }

    const dropdown = refDropDown.current
    dropdown.addEventListener('click', handlePhotoClick, true)

    return () => {
      dropdown.removeEventListener('click', handlePhotoClick, true)
    }
  }, [])

  return (
    <>
      <div
        className={`app-dropdown app-dropdown-multi-photo ${
          active ? 'active' : ''
        } ${fullWidth ? 'full-width' : ''} ${minWidth ? 'width-200' : ''}`}
        ref={refDropDown}
      >
        <AppDropdownChooseItems
          active={active}
          width={refDropDown.current?.offsetWidth}
          propName={propToShowInList}
          propValue={propToShowInInput}
          multiValueObj={multiValueObj}
          currentData={currentData}
          multiValue={value}
          onDeleteFromList={onDeleteFromList}
        />
        <AppInput
          onClick={() =>
            disabled
              ? false
              : setActive((prev) => {
                  if (!prev) refInput.current?.focus()
                  return !prev
                })
          }
          onChange={(value) => {
            if (!active) {
              setActive(true)
            }
            return setInputSearch(value)
          }}
          icoPosition={IcoPosition.right}
          value={inputValue}
          dropdownInput={!disabled}
          error={error}
          placeholder={placeholder}
          dropdownActive={active}
          disabled={true}
          label={label}
          fullWidth={fullWidth}
          border={border}
          required={required || require}
        />
        <div
          className={`dropdown-values-block ${
            active && !!currentData ? 'activate-dropdown-values-block' : ''
          }`}
        >
          {isFetching && <AppLoader loaderType={LoaderType.dropdown} />}
          <AppInput
            ref={refInput}
            value={inputSearch}
            onChange={setInputSearch}
            placeholder={'Поиск...'}
            fullWidth
            clear
            id={`drop-d-${id || ''}`}
          />
          {!!resetValueHandler && (
            <div
              className={`value-item reset-filter`}
              onClick={() => {
                onChange([])
                resetValueHandler()
              }}
            >
              {t('Очистить фильтр')}
            </div>
          )}
          <div className={'value-items'} id="zero">
            <AppScrollWrapperNew theme="dropdown">
              {currentData.map((item, index) => {
                const itemValue = propToShowInList
                  ? `${item[propToShowInList]}`
                  : `${item}`
                const itemPhoto = propToShowPhotoS3
                  ? `${
                      item[propToShowPhotoS3]
                        ? item[propToShowPhotoS3]
                        : propToShowPhoto
                        ? item[propToShowPhoto]
                          ? item[propToShowPhoto]
                          : ''
                        : ''
                    }`
                  : ''
                const itemWeight = propToShowWheight
                  ? `${item[propToShowWheight] ? item[propToShowWheight] : ''}`
                  : ''
                const multiItem = value.find(
                  (multiI: T) =>
                    propToShowInInput &&
                    item[propToShowInInput] === multiI[propToShowInInput],
                )

                return (
                  <div
                    onClick={() => onChangeHandler(item)}
                    className={'value-item value-item-multi-photo'}
                    id={`dd-value-${index}`}
                    key={`dropdown-active${index}`}
                  >
                    <Checkbox
                      value={!!multiItem}
                      onChange={() => {}}
                      name={`multi-value-${index}-${itemValue}`}
                      text={''}
                    />
                    <div className="dropdown-photo-descr-wrap">
                      <div>
                        <p className="dropdown-photo-subtitle">
                          {t('Наименование тары')}
                        </p>
                        <p className="dropdown-photo-title">{itemValue}</p>
                        <p className="dropdown-photo-weight">
                          {itemWeight} {t('кг')}
                        </p>
                      </div>
                      <div className="dropdown-photo-wrap">
                        {itemPhoto ? (
                          <img
                            className="dropdpwn-photo"
                            src={itemPhoto}
                            alt=""
                          />
                        ) : (
                          <i className="an-ico an-ico-photo-add"></i>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
              {!!data?.next && (
                <div onClick={(e) => e.stopPropagation()} className="mt-10">
                  <AppBtn
                    title="Показать ещё"
                    sized={AppBtnSize.fullWidth}
                    color={AppBtnColor.lightViolet}
                    radius={AppBtnRadius.base}
                    onClick={nextPageHandler}
                  />
                </div>
              )}
            </AppScrollWrapperNew>
          </div>
        </div>
      </div>
      {imagePopup.active && imagePopup.photo && (
        <AppImagePopup
          img={imagePopup.photo}
          title={imagePopup.title || ''}
          setActive={closeImagePopup}
          className="dropdown-photo-popup"
        />
      )}
    </>
  )
}

export default AppMultiDropdownWithPhotoAndPagination
